import React, { useEffect, useMemo } from "react";
import type { U3148OrganizationFormExtendedHandlers } from "../../../U3148OrganizationForm";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { NS, U3148ROLES } from "../../../../U3148";
import { useTranslation } from "react-i18next";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useU3148Context } from "../../../../U3148Context";
import type { useU2000LUStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000LUHandlers";
import { useNavigate } from "react-router-dom";
import { doDeleteRoleData, luHandleAppiError, type U3148Role } from "../../../../U3148Service";
import { useU2000DeleteHandlers } from "../../../../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";
import U3148OrganizationRoleDeleteDlg from "../../../common/U3148OrganizationRoleDeleteDlg";
import { u3148OrganizationRoleHandler } from "./U3148OrganizationRoleHandlers";

// Typage des props du composant
interface U3148OrganizationRolesProps {
  u3148OrganizationFormHandlers: U3148OrganizationFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "roleCode",
      label: t("role.lblRoleCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "roleDescription",
      label: t("role.lblRoleDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U3148OrganizationRoleBody: React.FC<U3148OrganizationRolesProps> = ({ u3148OrganizationFormHandlers: u3148OrganizationFormSHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const navigate = useNavigate();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const { luStateHandlers, gridOrgRoleStateHandlers } = useU3148Context();

  const u3148OrganizationRoleHandlers = u3148OrganizationRoleHandler({
    u2000AxiosAuthenticated,
    luStateHandlers: luStateHandlers as unknown as ReturnType<typeof useU2000LUStateHandlers>,
    gridStateHandlers: gridOrgRoleStateHandlers as unknown as ReturnType<typeof useU2000GridStateHandlers<U3148Role>>,
    t,
    navigate,
    organizationId: u3148OrganizationFormSHandlers.getData?.()?.id ?? "",
  });

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  const columns = useMemo(() => getColumnsDef(t), [t]);

  useEffect(() => {
    // Mettre à jour les colonnes seulement si nécessaire (évite boucle de rendu)
    try {
      const currentColumns = gridOrgRoleStateHandlers.getColumns?.() ?? [];
      if (JSON.stringify(currentColumns) !== JSON.stringify(columns)) {
        gridOrgRoleStateHandlers.setSize(9999);
        gridOrgRoleStateHandlers.setColumns(columns);
        gridOrgRoleStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
        gridOrgRoleStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
      }

      // Initialiser les données du tableau seulement si nécessaire
      const roles = (u3148OrganizationFormSHandlers.getData?.()?.roles as U3148Role[]) ?? [];
      const currentData = gridOrgRoleStateHandlers.getData?.() ?? [];
      if (JSON.stringify(currentData) !== JSON.stringify(roles)) {
        gridOrgRoleStateHandlers.setData(Array.isArray(roles) ? roles : []);
        gridOrgRoleStateHandlers.setTotalRows(Array.isArray(roles) ? roles.length : 0);
      }

      // Gérer les erreurs spécifiques au champ LogicalUnitActions
      const errorsField = u3148OrganizationFormSHandlers.getOtherErrors()?.roleActions;
      const normalizedErrors = errorsField !== undefined ? (Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []) : [];
      const currentErrors = gridOrgRoleStateHandlers.getErrors?.() ?? [];
      if (JSON.stringify(currentErrors) !== JSON.stringify(normalizedErrors)) {
        gridOrgRoleStateHandlers.setErrors(normalizedErrors);
      }
    } catch (e) {
      // En cas d'erreur de sérialisation, fallback to direct set to avoid bloquer l'UI
      const cols = columns;
      gridOrgRoleStateHandlers.setColumns(cols);
      const organizationRoles = (u3148OrganizationFormSHandlers.getData?.()?.roles as U3148Role[]) ?? [];
      gridOrgRoleStateHandlers.setData(Array.isArray(organizationRoles) ? organizationRoles : []);
      gridOrgRoleStateHandlers.setTotalRows(Array.isArray(organizationRoles) ? organizationRoles.length : 0);
      const errorsField = u3148OrganizationFormSHandlers.getOtherErrors()?.roleActions;
      gridOrgRoleStateHandlers.setErrors(errorsField !== undefined ? (Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []) : []);
    }
  }, [u3148OrganizationFormSHandlers.getData(), t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u3148OrganizationRoleHandlers.dataChangedEvent === 0) return;

    const organizationRoles = gridOrgRoleStateHandlers.getData();
    u3148OrganizationFormSHandlers.setData({
      ...u3148OrganizationFormSHandlers.getData?.(),
      roles: organizationRoles,
    });
  }, [u3148OrganizationRoleHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridOrgRoleStateHandlers.getSelectedRows();
    return u3148OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridOrgRoleStateHandlers.getSelectedRows();
    return u3148OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || selectedRows.length === 0 ? true : false;
  }

  // Gestion des Delete Record avec confirmation
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridOrgRoleStateHandlers ? gridOrgRoleStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (roleId, row) => {
      return await doDeleteRoleData(u2000AxiosAuthenticated, u3148OrganizationFormSHandlers.getData().id, roleId, row);
    },

    onSuccess: (deletedRows) => {
      let newData = gridOrgRoleStateHandlers.getData();
      let totalRows = gridOrgRoleStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["roleCode"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });

      gridOrgRoleStateHandlers.setData(newData);
      gridOrgRoleStateHandlers.setTotalRows(totalRows);
      u3148OrganizationRoleHandlers.setDataChangedEvent(u3148OrganizationRoleHandlers.dataChangedEvent + 1);
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u3148OrganizationRoleHandlers.onAdd,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: u3148OrganizationFormSHandlers.getFormState?.() === U2000FormStateEnumState.EDIT,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u3148OrganizationRoleHandlers.onModify,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handleDelete.handleDelete,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3148OrganizationRoleHandlers]
  );

  // Render
  return (
    <>
      <U3148OrganizationRoleDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridOrgRoleStateHandlers}
        rowClickFn={u3148OrganizationRoleHandlers.onRowClick as unknown as (row: any) => void}
        allowRowSelection={u3148OrganizationFormSHandlers.getFormState?.() !== U2000FormStateEnumState.EDIT}
        label={t("lblRole", {
          count: u3148OrganizationRoleHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3148OrganizationRoleBody;
